import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, CalendarDays } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Section, NativeSelect, MarginBadge, Empty } from "../common";
import { useFetch } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { money, fdt } from "../../lib/format";

function Assignments({ q }) {
  const { t } = useT();
  const { can } = useAuth();
  const { data } = useFetch(can("schedule:read") ? `/schedule?quotation_id=${q.id}` : null);
  if (!can("schedule:read")) return null;
  return (
    <Section title={t("sch.assignments")} testid="section-assignments" right={<Button asChild size="sm" variant="outline"><Link to="/schedule" data-testid="go-schedule-btn"><CalendarDays className="h-4 w-4" />{t("sch.title")}</Link></Button>}>
      <table className="dense-table w-full">
        <thead><tr><th>{t("nav.technicians")}</th><th>{t("sch.start")}</th><th>{t("sch.end")}</th><th>{t("sch.location")}</th></tr></thead>
        <tbody>{(data || []).map((e) => <tr key={e.id}><td className="font-medium">{e.technician_name}</td><td>{fdt(e.start)}</td><td>{fdt(e.end)}</td><td>{e.location}</td></tr>)}</tbody>
      </table>
      {data && !data.length && <Empty>{t("sch.noAssignments")}</Empty>}
    </Section>
  );
}

export default function TechTab({ q, d, set, m, locked, showCost }) {
  const { t } = useT();
  const [tech, setTech] = useState("");
  const sel = new Set(d.machine_ids || []);
  const techs = d.technicians || [];
  const toggle = (id, c) => { const s = new Set(sel); c ? s.add(id) : s.delete(id); set({ machine_ids: [...s] }); };
  const add = () => {
    const x = (m.technicians || []).find((y) => y.id === tech);
    if (!x || techs.some((y) => y.technician_id === x.id)) return;
    set({ technicians: [...techs, { technician_id: x.id, name: x.name, level: x.level, days: 1, role: "", notes: "" }] });
    setTech("");
  };
  const upd = (i, p) => set({ technicians: techs.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const mname = Object.fromEntries((m.machines || []).map((x) => [x.id, `${x.model} #${x.serial_number}`]));
  return (
    <div className="grid gap-4 xl:grid-cols-2 [&>*]:min-w-0">
      <Section title={t("ws.customerMachines")} testid="section-machines">
        <table className="dense-table w-full">
          <thead><tr><th /><th>{t("mach.model")}</th><th>{t("mach.type")}</th><th>{t("mach.serial")}</th><th>{t("mach.year")}</th></tr></thead>
          <tbody>
            {(m.machines || []).map((x) => (
              <tr key={x.id}>
                <td><Checkbox data-testid={`machine-toggle-${x.serial_number}`} disabled={locked} checked={sel.has(x.id)} onCheckedChange={(c) => toggle(x.id, c)} /></td>
                <td className="font-medium">{x.model}</td><td>{x.machine_type}</td><td className="font-mono">{x.serial_number}</td><td>{x.year}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!(m.machines || []).length && <Empty>{t("common.empty")}</Empty>}
      </Section>
      <Section title={t("ws.proposedTechs")} testid="section-techs" right={!locked && (
        <div className="flex flex-wrap gap-2">
          <div className="w-full sm:w-52"><NativeSelect testid="tech-select" value={tech} placeholder="—" onChange={(v) => setTech(v || "")} options={(m.technicians || []).map((x) => ({ value: x.id, label: `${x.name} (${x.level || ""})` }))} /></div>
          <Button size="sm" data-testid="add-tech-btn" onClick={add} disabled={!tech}><Plus className="h-4 w-4" />{t("ws.addTech")}</Button>
        </div>)}>
        <table className="dense-table w-full">
          <thead><tr><th>{t("common.name")}</th><th>{t("tech.level")}</th><th>{t("ws.days")}</th><th>{t("ws.role")}</th><th /></tr></thead>
          <tbody>
            {techs.map((x, i) => (
              <tr key={x.technician_id} data-testid={`tech-row-${i}`}>
                <td className="font-medium">{x.name}</td><td>{x.level}</td>
                <td><input type="number" min="0" disabled={locked} className="cell-input num w-16" value={x.days} onChange={(e) => upd(i, { days: Number(e.target.value) })} data-testid={`tech-days-${i}`} /></td>
                <td><input disabled={locked} className="cell-input" value={x.role || ""} onChange={(e) => upd(i, { role: e.target.value })} data-testid={`tech-role-${i}`} /></td>
                <td>{!locked && <button onClick={() => set({ technicians: techs.filter((_, j) => j !== i) })} data-testid={`tech-delete-${i}`} className="text-neutral-400 hover:text-[#db0720]"><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!techs.length && <Empty>{t("common.empty")}</Empty>}
      </Section>
      {showCost && q.calc.machine_allocation && (
        <Section title={t("ws.allocation")} testid="section-allocation">
          <table className="dense-table w-full">
            <thead><tr><th>{t("mach.model")}</th><th className="text-right">{t("ws.net")}</th><th className="text-right">{t("ws.cost")}</th><th className="text-right">{t("ws.gp")}</th><th>{t("q.margin")}</th></tr></thead>
            <tbody>
              {q.calc.machine_allocation.map((a) => (
                <tr key={a.machine_id}>
                  <td>{a.machine_id === "_unassigned" ? <i className="text-neutral-500">{t("ws.unassigned")}</i> : mname[a.machine_id] || a.machine_id}</td>
                  <td className="num">{money(a.net)}</td><td className="num">{money(a.cost)}</td><td className="num">{money(a.gross_profit)}</td>
                  <td><MarginBadge value={a.net ? (a.gross_profit / a.net) * 100 : null} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
      <Assignments q={q} />
    </div>
  );
}
